import { useState, useEffect } from 'react';
import { Search, SlidersHorizontal } from 'lucide-react';
import clsx from 'clsx';
import TemplateCard from '../components/TemplateCard';
import { fetchTemplates } from '../lib/api';
import type { Template } from '../types/wizard';

const categories = [
  'All',
  'Frontend',
  'Backend',
  'Full Stack',
  'Data/ML',
  'DevOps',
  'Mobile',
  'API',
];

const sortOptions = [
  { value: 'stars', label: 'Most Stars' },
  { value: 'recent', label: 'Most Recent' },
  { value: 'forks', label: 'Most Forked' },
];

// Demo templates for when the API is not available
const demoTemplates: Template[] = [
  {
    id: '1',
    name: 'Next.js Enterprise',
    description:
      'Production-ready Next.js 14 with TypeScript, Tailwind CSS, Prisma ORM, and comprehensive testing setup including unit, integration, and E2E tests.',
    category: 'Frontend',
    tags: ['next.js', 'typescript', 'prisma', 'tailwind'],
    stars: 342,
    forks: 89,
    author: 'codeforge-team',
    createdAt: '2024-11-01T00:00:00Z',
    updatedAt: '2024-11-20T00:00:00Z',
    techStack: ['Next.js', 'TypeScript', 'Prisma', 'Tailwind CSS', 'PostgreSQL'],
    wizardData: {
      projectContext: {
        name: 'enterprise-nextjs-app',
        goal: 'Build a production-ready SaaS platform with enterprise-grade auth, multi-tenancy, and observability',
        description: 'Full-stack Next.js 14 application with App Router, TypeScript, Tailwind CSS, Prisma ORM, NextAuth.js authentication, and comprehensive CI/CD pipeline.',
        industry: 'Technology / SaaS',
        compliance: ['SOC 2', 'GDPR'],
      },
      techStack: {
        languages: ['TypeScript'],
        frameworks: ['Next.js', 'React'],
        databases: ['PostgreSQL', 'Redis'],
        cloudProvider: 'AWS',
        infrastructure: ['Docker', 'Terraform'],
        packageManager: 'pnpm',
        monorepo: false,
        monorepoTool: '',
      },
      teamStructure: {
        size: 'Medium (6-15)',
        seniorityMix: { junior: 15, mid: 35, senior: 35, lead: 15 },
        claudeUsers: ['All developers'],
        dedicatedReviewers: true,
        timezones: 'Americas + Europe',
      },
      gitWorkflow: {
        branchingStrategy: 'trunk-based',
        branchNaming: 'type/ticket-desc',
        commitStandard: 'conventional',
        signedCommits: false,
        prRequirements: ['Code review required', 'Tests passing', 'Coverage threshold met', 'No unresolved comments'],
        protectedBranches: ['main'],
      },
      codingStandards: {
        linting: 'ESLint (strict)',
        formatting: 'Prettier',
        testRequirements: ['Unit tests required', 'Integration tests required', 'E2E tests required'],
        coverageThreshold: 80,
        architecture: 'Modular Monolith',
        sensitivePaths: ['src/lib/auth/*', 'prisma/migrations/*', '.env*'],
        dependencyPolicy: 'Moderate (review required)',
        maxFileLength: 300,
        namingConventions: ['camelCase for variables', 'PascalCase for classes/components', 'kebab-case for file names', 'Prefix hooks with use', 'Prefix boolean with is/has/should'],
        customConventions: 'Use server actions for mutations. Prefer React Server Components for data fetching. Co-locate tests next to source files. All API responses use { data, error, meta } envelope pattern.',
      },
      mcpConfig: {
        ticketSystem: 'Linear',
        documentation: ['Notion', 'Swagger/OpenAPI'],
        communication: ['Slack'],
        sourceControl: 'GitHub',
        cloudConsole: 'AWS Console',
        monitoring: ['Datadog', 'Sentry'],
        customServers: [],
      },
      cicd: {
        platform: 'GitHub Actions',
        stages: ['Lint', 'Type Check', 'Unit Tests', 'Integration Tests', 'Build', 'Staging Deploy', 'E2E Tests'],
        autoDeploy: 'staging',
        aiCodeFlag: true,
        codeOwnership: true,
      },
      plugins: ['plannotator'],
    },
  },
  {
    id: '2',
    name: 'Spring Boot Microservices',
    description:
      'Java/Kotlin microservice architecture with Spring Boot, Apache Kafka for event streaming, and Kubernetes deployment configurations.',
    category: 'Backend',
    tags: ['java', 'spring-boot', 'kafka', 'kubernetes'],
    stars: 256,
    forks: 67,
    author: 'codeforge-team',
    createdAt: '2024-10-15T00:00:00Z',
    updatedAt: '2024-11-18T00:00:00Z',
    techStack: ['Java', 'Spring Boot', 'Kafka', 'Kubernetes', 'PostgreSQL'],
    wizardData: {
      projectContext: {
        name: 'spring-boot-microservices',
        goal: 'Build an event-driven microservice platform with domain-bounded services, Kafka event streaming, and Kubernetes-native deployment',
        description: 'Enterprise microservice architecture using Spring Boot 3.2 and Java 21, with Kafka for inter-service communication, PostgreSQL per-service databases, Redis for caching and session management, and Kubernetes deployment via Helm charts. Includes API gateway, service discovery, distributed tracing, and centralized logging.',
        industry: 'Financial Services / Fintech',
        compliance: ['SOC 2', 'PCI DSS'],
      },
      techStack: {
        languages: ['Java', 'Kotlin'],
        frameworks: ['Spring Boot'],
        databases: ['PostgreSQL', 'Redis'],
        cloudProvider: 'AWS',
        infrastructure: ['Docker', 'Kubernetes', 'Helm', 'Terraform'],
        packageManager: 'gradle',
        monorepo: true,
        monorepoTool: 'Gradle multi-module',
      },
      teamStructure: {
        size: 'Medium (6-15)',
        seniorityMix: { junior: 10, mid: 30, senior: 40, lead: 20 },
        claudeUsers: ['All backend developers'],
        dedicatedReviewers: true,
        timezones: 'Europe + Americas (partial overlap)',
      },
      gitWorkflow: {
        branchingStrategy: 'github-flow',
        branchNaming: 'type/ticket-desc',
        commitStandard: 'conventional',
        signedCommits: true,
        prRequirements: ['Code review required', 'Tests passing', 'At least 2 approvals', 'Security scan passing'],
        protectedBranches: ['main', 'release/*'],
      },
      codingStandards: {
        linting: 'Checkstyle + ktlint',
        formatting: 'google-java-format + ktlint',
        testRequirements: ['Unit tests required', 'Integration tests required', 'Contract tests (Pact)'],
        coverageThreshold: 80,
        architecture: 'Hexagonal Architecture (Ports & Adapters)',
        sensitivePaths: ['**/security/**', '**/auth/**', 'infrastructure/production/**', '*.properties'],
        dependencyPolicy: 'Conservative (approved list only)',
        maxFileLength: 300,
        namingConventions: ['PascalCase for classes', 'camelCase for methods/variables', 'SCREAMING_SNAKE_CASE for constants', 'kebab-case for module names'],
        customConventions: 'Each microservice must have its own Gradle module, Dockerfile, Helm chart, and OpenAPI spec. Use Spring Profiles for environment configuration. All inter-service communication goes through Kafka — no direct REST calls between services. Use Testcontainers for integration tests. Every domain event must have a schema in the shared schema registry.',
      },
      mcpConfig: {
        ticketSystem: 'Jira',
        documentation: ['Confluence', 'Markdown in repo'],
        communication: ['Slack'],
        sourceControl: 'GitHub',
        cloudConsole: 'AWS Console',
        monitoring: ['Prometheus', 'Grafana', 'Jaeger', 'ELK Stack'],
        customServers: [],
      },
      cicd: {
        platform: 'GitHub Actions',
        stages: ['Lint', 'Compile', 'Unit Tests', 'Integration Tests', 'Contract Tests', 'Security Scan', 'Build Docker Image', 'Deploy Staging', 'E2E Tests', 'Deploy Production'],
        autoDeploy: 'staging',
        aiCodeFlag: true,
        codeOwnership: true,
      },
      plugins: ['plannotator'],
    },
  },
  {
    id: '3',
    name: 'Python ML Pipeline',
    description:
      'Machine learning project setup with PyTorch, MLflow for experiment tracking, DVC for data versioning, and automated model training pipelines.',
    category: 'Data/ML',
    tags: ['python', 'pytorch', 'mlflow', 'dvc'],
    stars: 198,
    forks: 45,
    author: 'ml-team',
    createdAt: '2024-09-20T00:00:00Z',
    updatedAt: '2024-11-15T00:00:00Z',
    techStack: ['Python', 'PyTorch', 'MLflow', 'DVC', 'Docker'],
    wizardData: {
      projectContext: {
        name: 'ml-pipeline',
        goal: 'Build a reproducible ML pipeline with experiment tracking, data versioning, automated training, and model serving',
        description: 'End-to-end machine learning pipeline using PyTorch for model training, MLflow for experiment tracking and model registry, DVC for data versioning and pipeline orchestration, and FastAPI for model serving. Includes feature engineering, hyperparameter tuning, model evaluation, A/B testing infrastructure, and GPU-accelerated training on AWS.',
        industry: 'Technology / SaaS',
        compliance: ['SOC 2', 'GDPR'],
      },
      techStack: {
        languages: ['Python'],
        frameworks: ['FastAPI'],
        databases: ['PostgreSQL', 'Redis'],
        cloudProvider: 'AWS',
        infrastructure: ['Docker', 'Kubernetes', 'Terraform'],
        packageManager: 'uv',
        monorepo: false,
      },
      teamStructure: {
        size: 'Small (2-5)',
        seniorityMix: { junior: 10, mid: 25, senior: 45, lead: 20 },
        claudeUsers: ['ML engineers', 'Data scientists'],
        dedicatedReviewers: true,
        timezones: 'Americas (overlapping)',
      },
      gitWorkflow: {
        branchingStrategy: 'github-flow',
        branchNaming: 'type/ticket-desc',
        commitStandard: 'conventional',
        signedCommits: false,
        prRequirements: ['Code review required', 'Tests passing', 'At least 1 approval'],
        protectedBranches: ['main'],
      },
      codingStandards: {
        linting: 'ruff',
        formatting: 'ruff format',
        testRequirements: ['Unit tests required', 'Integration tests required'],
        coverageThreshold: 75,
        architecture: 'Pipeline Architecture (DAG-based)',
        sensitivePaths: ['configs/production/**', 'models/**', '.env*', 'data/**'],
        dependencyPolicy: 'Moderate (review new deps)',
        maxFileLength: 300,
        namingConventions: ['snake_case for files and functions', 'PascalCase for classes', 'SCREAMING_SNAKE_CASE for constants'],
        customConventions: 'All experiments must be tracked in MLflow with reproducible configs. Use DVC for data and model artifact versioning. Notebooks are for exploration only — production code goes in src/. Type hints required on all public functions. Use Pydantic for config validation. Pin all dependency versions in pyproject.toml.',
      },
      mcpConfig: {
        ticketSystem: 'GitHub Issues',
        documentation: ['Markdown in repo', 'Notion'],
        communication: ['Slack'],
        sourceControl: 'GitHub',
        cloudConsole: 'AWS Console',
        monitoring: ['Prometheus', 'Grafana', 'MLflow'],
        customServers: [],
      },
      cicd: {
        platform: 'GitHub Actions',
        stages: ['Lint', 'Type Check', 'Unit Tests', 'Integration Tests', 'Build Docker Image', 'Train Smoke Test', 'Deploy Staging', 'Deploy Production'],
        autoDeploy: 'staging',
        aiCodeFlag: true,
        codeOwnership: true,
      },
      plugins: ['plannotator'],
    },
  },
  {
    id: '4',
    name: 'Django SaaS',
    description:
      'Multi-tenant SaaS boilerplate with Django, Celery for async tasks, Redis for caching, and Stripe integration for billing.',
    category: 'Full Stack',
    tags: ['python', 'django', 'celery', 'redis', 'stripe'],
    stars: 187,
    forks: 52,
    author: 'saas-builders',
    createdAt: '2024-10-01T00:00:00Z',
    updatedAt: '2024-11-12T00:00:00Z',
    techStack: ['Python', 'Django', 'Celery', 'Redis', 'PostgreSQL'],
    wizardData: {
      projectContext: {
        name: 'django-saas-platform',
        goal: 'Build a multi-tenant SaaS platform with subscription billing, async task processing, and a REST/GraphQL API',
        description: 'Multi-tenant SaaS application using Django 5.0 with Django REST Framework and Strawberry GraphQL. Celery for background task processing (email, reports, webhooks), Redis for caching and Celery broker, PostgreSQL with row-level security for multi-tenancy. Stripe integration for subscription billing and usage-based metering. Includes tenant onboarding, role-based access control, audit logging, and white-label support.',
        industry: 'Technology / SaaS',
        compliance: ['SOC 2', 'GDPR'],
      },
      techStack: {
        languages: ['Python'],
        frameworks: ['Django'],
        databases: ['PostgreSQL', 'Redis'],
        cloudProvider: 'AWS',
        infrastructure: ['Docker', 'Kubernetes', 'Terraform'],
        packageManager: 'uv',
        monorepo: false,
        monorepoTool: '',
      },
      teamStructure: {
        size: 'Medium (6-15)',
        seniorityMix: { junior: 15, mid: 35, senior: 35, lead: 15 },
        claudeUsers: ['Backend developers', 'Full-stack developers'],
        dedicatedReviewers: true,
        timezones: 'Europe + Americas (partial overlap)',
      },
      gitWorkflow: {
        branchingStrategy: 'github-flow',
        branchNaming: 'type/ticket-desc',
        commitStandard: 'conventional',
        signedCommits: false,
        prRequirements: ['Code review required', 'Tests passing', 'At least 1 approval'],
        protectedBranches: ['main'],
      },
      codingStandards: {
        linting: 'ruff',
        formatting: 'ruff format',
        testRequirements: ['Unit tests required', 'Integration tests required', 'API contract tests'],
        coverageThreshold: 85,
        architecture: 'Django Apps (modular monolith)',
        sensitivePaths: ['**/billing/**', '**/auth/**', 'config/production/**', '.env*'],
        dependencyPolicy: 'Moderate (review new deps)',
        maxFileLength: 300,
        namingConventions: ['snake_case for files and functions', 'PascalCase for classes', 'SCREAMING_SNAKE_CASE for constants', 'kebab-case for URLs'],
        customConventions: 'Each Django app is self-contained with its own models, serializers, views, and tests. Use Django signals sparingly — prefer explicit service layer calls. All API endpoints must have OpenAPI schema via drf-spectacular. Celery tasks must be idempotent. Use factory_boy for test fixtures. Stripe webhooks must be verified and idempotent. All tenant data access must go through the tenant-aware manager.',
      },
      mcpConfig: {
        ticketSystem: 'GitHub Issues',
        documentation: ['Markdown in repo', 'Notion'],
        communication: ['Slack'],
        sourceControl: 'GitHub',
        cloudConsole: 'AWS Console',
        monitoring: ['Sentry', 'Prometheus', 'Grafana'],
        customServers: [],
      },
      cicd: {
        platform: 'GitHub Actions',
        stages: ['Lint', 'Type Check', 'Unit Tests', 'Integration Tests', 'Security Scan', 'Build Docker Image', 'Deploy Staging', 'E2E Tests', 'Deploy Production'],
        autoDeploy: 'staging',
        aiCodeFlag: true,
        codeOwnership: true,
      },
      plugins: ['plannotator'],
    },
  },
  {
    id: '5',
    name: 'Go API Gateway',
    description:
      'High-performance API gateway built with Go and Chi router, featuring rate limiting, JWT auth, and comprehensive observability.',
    category: 'API',
    tags: ['go', 'chi', 'jwt', 'api-gateway'],
    stars: 145,
    forks: 38,
    author: 'go-enthusiasts',
    createdAt: '2024-10-20T00:00:00Z',
    updatedAt: '2024-11-19T00:00:00Z',
    techStack: ['Go', 'Chi', 'Docker', 'Prometheus', 'Redis'],
    wizardData: {
      projectContext: {
        name: 'go-api-gateway',
        goal: 'Build a high-performance API gateway with rate limiting, JWT authentication, request routing, and comprehensive observability',
        description: 'API gateway built with Go 1.22 and Chi router, serving as the single entry point for a microservices architecture. Features include JWT authentication and RBAC, per-client rate limiting with Redis, request/response transformation, circuit breaker pattern, distributed tracing with OpenTelemetry, health checks, and graceful shutdown. Designed for low-latency, high-throughput traffic routing.',
        industry: 'Technology / SaaS',
        compliance: ['SOC 2'],
      },
      techStack: {
        languages: ['Go'],
        frameworks: ['Chi'],
        databases: ['PostgreSQL', 'Redis'],
        cloudProvider: 'AWS',
        infrastructure: ['Docker', 'Kubernetes', 'Helm', 'Terraform'],
        packageManager: 'go modules',
        monorepo: false,
        monorepoTool: '',
      },
      teamStructure: {
        size: 'Small (2-5)',
        seniorityMix: { junior: 5, mid: 25, senior: 50, lead: 20 },
        claudeUsers: ['All backend developers'],
        dedicatedReviewers: true,
        timezones: 'Americas (overlapping)',
      },
      gitWorkflow: {
        branchingStrategy: 'github-flow',
        branchNaming: 'type/ticket-desc',
        commitStandard: 'conventional',
        signedCommits: false,
        prRequirements: ['Code review required', 'Tests passing', 'At least 1 approval', 'Lint passing'],
        protectedBranches: ['main'],
      },
      codingStandards: {
        linting: 'golangci-lint',
        formatting: 'gofmt + goimports',
        testRequirements: ['Unit tests required', 'Integration tests required', 'Benchmark tests for hot paths'],
        coverageThreshold: 80,
        architecture: 'Clean Architecture (handler → service → repository)',
        sensitivePaths: ['internal/auth/**', 'config/production/**', '*.pem', '.env*'],
        dependencyPolicy: 'Conservative (minimal dependencies)',
        maxFileLength: 300,
        namingConventions: ['camelCase for unexported', 'PascalCase for exported', 'snake_case for files', 'ALL_CAPS for env vars'],
        customConventions: 'Follow Effective Go and Go Code Review Comments. Use interfaces for dependency injection. Table-driven tests required. No init() functions. Context propagation required on all handlers. Errors must be wrapped with fmt.Errorf and %w. Use structured logging with slog. All middleware must be composable Chi middleware.',
      },
      mcpConfig: {
        ticketSystem: 'GitHub Issues',
        documentation: ['Markdown in repo'],
        communication: ['Slack'],
        sourceControl: 'GitHub',
        cloudConsole: 'AWS Console',
        monitoring: ['Prometheus', 'Grafana', 'Jaeger'],
        customServers: [],
      },
      cicd: {
        platform: 'GitHub Actions',
        stages: ['Lint', 'Vet', 'Unit Tests', 'Integration Tests', 'Race Detection', 'Build Binary', 'Build Docker Image', 'Deploy Staging', 'Deploy Production'],
        autoDeploy: 'staging',
        aiCodeFlag: true,
        codeOwnership: true,
      },
      plugins: ['plannotator'],
    },
  },
  {
    id: '6',
    name: 'React Native Mobile',
    description:
      'Cross-platform mobile app template with React Native, Expo, TypeScript, and integrated CI/CD for iOS and Android deployment.',
    category: 'Mobile',
    tags: ['react-native', 'expo', 'typescript', 'mobile'],
    stars: 134,
    forks: 41,
    author: 'mobile-devs',
    createdAt: '2024-09-15T00:00:00Z',
    updatedAt: '2024-11-10T00:00:00Z',
    techStack: ['React Native', 'Expo', 'TypeScript', 'Firebase'],
    wizardData: {
      projectContext: {
        name: 'react-native-mobile-app',
        goal: 'Build a cross-platform mobile app with native performance, offline support, push notifications, and streamlined CI/CD for App Store and Google Play',
        description: 'Cross-platform mobile application using React Native with Expo SDK 51, TypeScript for type safety, and Firebase for backend services (authentication, Firestore, Cloud Functions, push notifications). Features include offline-first architecture with WatermelonDB, deep linking, biometric authentication, in-app purchases, analytics, and crash reporting. EAS Build for CI/CD with separate staging and production channels.',
        industry: 'Technology / Consumer',
        compliance: ['GDPR', 'App Store Guidelines', 'Google Play Policies'],
      },
      techStack: {
        languages: ['TypeScript'],
        frameworks: ['React Native'],
        databases: ['Firebase Firestore', 'WatermelonDB'],
        cloudProvider: 'GCP',
        infrastructure: ['EAS Build', 'Firebase', 'Fastlane'],
        packageManager: 'pnpm',
        monorepo: false,
        monorepoTool: '',
      },
      teamStructure: {
        size: 'Small (2-5)',
        seniorityMix: { junior: 15, mid: 35, senior: 35, lead: 15 },
        claudeUsers: ['Mobile developers', 'Full-stack developers'],
        dedicatedReviewers: true,
        timezones: 'Americas (overlapping)',
      },
      gitWorkflow: {
        branchingStrategy: 'github-flow',
        branchNaming: 'type/ticket-desc',
        commitStandard: 'conventional',
        signedCommits: false,
        prRequirements: ['Code review required', 'Tests passing', 'At least 1 approval'],
        protectedBranches: ['main'],
      },
      codingStandards: {
        linting: 'ESLint (react-native-community config)',
        formatting: 'Prettier',
        testRequirements: ['Unit tests required', 'Component tests (React Native Testing Library)', 'E2E tests (Detox)'],
        coverageThreshold: 75,
        architecture: 'Feature-based (screens → components → hooks → services)',
        sensitivePaths: ['src/services/auth/**', 'src/config/**', 'google-services.json', 'GoogleService-Info.plist', '.env*'],
        dependencyPolicy: 'Moderate (review new native modules)',
        maxFileLength: 250,
        namingConventions: ['PascalCase for components/screens', 'camelCase for hooks/utils', 'kebab-case for files', 'SCREAMING_SNAKE_CASE for constants'],
        customConventions: 'Each feature gets its own directory under src/features/ with screens, components, hooks, and types. Use React Query for server state and Zustand for client state. All navigation typed with React Navigation. Native modules must have both iOS and Android implementations. Use Expo Modules API for custom native code. All text must go through i18n. Accessibility labels required on all interactive elements.',
      },
      mcpConfig: {
        ticketSystem: 'GitHub Issues',
        documentation: ['Markdown in repo'],
        communication: ['Slack'],
        sourceControl: 'GitHub',
        cloudConsole: 'Firebase Console',
        monitoring: ['Sentry', 'Firebase Analytics', 'Firebase Crashlytics'],
        customServers: [],
      },
      cicd: {
        platform: 'GitHub Actions',
        stages: ['Lint', 'Type Check', 'Unit Tests', 'Component Tests', 'EAS Build (iOS)', 'EAS Build (Android)', 'Detox E2E', 'Deploy Staging', 'Deploy Production'],
        autoDeploy: 'staging',
        aiCodeFlag: true,
        codeOwnership: true,
      },
      plugins: ['plannotator'],
    },
  },
  {
    id: '7',
    name: 'Terraform AWS Platform',
    description:
      'Complete AWS infrastructure as code with Terraform modules for VPC, ECS, RDS, and monitoring with custom CloudWatch dashboards.',
    category: 'DevOps',
    tags: ['terraform', 'aws', 'ecs', 'infrastructure'],
    stars: 167,
    forks: 54,
    author: 'platform-team',
    createdAt: '2024-10-05T00:00:00Z',
    updatedAt: '2024-11-17T00:00:00Z',
    techStack: ['Terraform', 'AWS', 'Docker', 'CloudWatch'],
    wizardData: {
      projectContext: {
        name: 'aws-platform-iac',
        goal: 'Build a production-grade AWS infrastructure platform with reusable Terraform modules, automated provisioning, and comprehensive monitoring',
        description: 'Complete AWS infrastructure as code using Terraform modules for VPC networking, ECS container orchestration, RDS database clusters, and CloudWatch monitoring dashboards. Includes self-service provisioning for developer environments, cost management, and security compliance automation.',
        industry: 'Technology / SaaS',
        compliance: ['SOC 2', 'ISO 27001'],
      },
      techStack: {
        languages: ['Go', 'Python'],
        frameworks: [],
        databases: ['PostgreSQL', 'Redis'],
        cloudProvider: 'AWS',
        infrastructure: ['Docker', 'Kubernetes', 'Terraform', 'Helm', 'ArgoCD'],
        packageManager: 'go modules',
        monorepo: true,
        monorepoTool: 'Turborepo',
      },
      teamStructure: {
        size: 'Small (2-5)',
        seniorityMix: { junior: 5, mid: 20, senior: 50, lead: 25 },
        claudeUsers: ['DevOps/SRE team'],
        dedicatedReviewers: true,
        timezones: 'Americas (overlapping)',
      },
      gitWorkflow: {
        branchingStrategy: 'github-flow',
        branchNaming: 'type/ticket-desc',
        commitStandard: 'conventional',
        signedCommits: true,
        prRequirements: ['Code review required', 'Tests passing', 'At least 2 approvals', 'Security scan passing'],
        protectedBranches: ['main'],
      },
      codingStandards: {
        linting: 'golangci-lint',
        formatting: 'gofmt',
        testRequirements: ['Unit tests required', 'Integration tests required'],
        coverageThreshold: 70,
        architecture: 'Clean Architecture',
        sensitivePaths: ['modules/security/*', 'environments/production/*', '*.tfvars', '.env*'],
        dependencyPolicy: 'Conservative (approved list only)',
        maxFileLength: 250,
        namingConventions: ['snake_case for file names', 'SCREAMING_SNAKE_CASE for constants', 'camelCase for variables'],
        customConventions: 'All Terraform modules must have README.md, variables.tf, outputs.tf, and versions.tf. Use terraform-docs for automated documentation. Pin all provider versions to exact patch. Use workspaces for environment separation. Tag all AWS resources with project, environment, team, and cost-center tags. Never use inline IAM policies; always use managed policies.',
      },
      mcpConfig: {
        ticketSystem: 'GitHub Issues',
        documentation: ['Markdown in repo', 'Confluence'],
        communication: ['Slack'],
        sourceControl: 'GitHub',
        cloudConsole: 'AWS Console',
        monitoring: ['Prometheus', 'Grafana', 'PagerDuty', 'CloudWatch'],
        customServers: [],
      },
      cicd: {
        platform: 'GitHub Actions',
        stages: ['Lint', 'Type Check', 'Unit Tests', 'Security Scan', 'Build', 'Staging Deploy', 'Integration Tests', 'Production Deploy'],
        autoDeploy: 'staging',
        aiCodeFlag: true,
        codeOwnership: true,
      },
      plugins: ['plannotator'],
    },
  },
  {
    id: '8',
    name: 'Rust Actix Web API',
    description:
      'Blazing fast REST API with Actix Web, SQLx for async database access, and comprehensive error handling with type-safe routes.',
    category: 'Backend',
    tags: ['rust', 'actix-web', 'sqlx', 'api'],
    stars: 112,
    forks: 28,
    author: 'rust-community',
    createdAt: '2024-11-05T00:00:00Z',
    updatedAt: '2024-11-19T00:00:00Z',
    techStack: ['Rust', 'Actix Web', 'SQLx', 'PostgreSQL', 'Docker'],
    wizardData: {
      projectContext: {
        name: 'rust-actix-api',
        goal: 'Build a high-performance, type-safe REST API with async database access, structured error handling, and production-ready observability',
        description: 'REST API built with Actix Web 4 and Rust, featuring SQLx for compile-time verified async PostgreSQL queries, structured error handling with thiserror/anyhow, JWT authentication, rate limiting, request validation with validator crate, OpenTelemetry tracing, Prometheus metrics, and comprehensive integration tests. Designed for maximum throughput and memory safety.',
        industry: 'Technology / SaaS',
        compliance: ['SOC 2'],
      },
      techStack: {
        languages: ['Rust'],
        frameworks: ['Actix Web'],
        databases: ['PostgreSQL', 'Redis'],
        cloudProvider: 'AWS',
        infrastructure: ['Docker', 'Kubernetes', 'Terraform'],
        packageManager: 'cargo',
        monorepo: false,
        monorepoTool: '',
      },
      teamStructure: {
        size: 'Small (2-5)',
        seniorityMix: { junior: 5, mid: 20, senior: 50, lead: 25 },
        claudeUsers: ['All backend developers'],
        dedicatedReviewers: true,
        timezones: 'Europe (overlapping)',
      },
      gitWorkflow: {
        branchingStrategy: 'github-flow',
        branchNaming: 'type/ticket-desc',
        commitStandard: 'conventional',
        signedCommits: false,
        prRequirements: ['Code review required', 'Tests passing', 'At least 1 approval', 'Clippy clean'],
        protectedBranches: ['main'],
      },
      codingStandards: {
        linting: 'clippy (pedantic)',
        formatting: 'rustfmt',
        testRequirements: ['Unit tests required', 'Integration tests with test database', 'Property-based tests (proptest)'],
        coverageThreshold: 80,
        architecture: 'Layered (handler → service → repository)',
        sensitivePaths: ['src/auth/**', 'config/production/**', '*.pem', '.env*'],
        dependencyPolicy: 'Conservative (audit all new crates)',
        maxFileLength: 300,
        namingConventions: ['snake_case for functions/variables/modules', 'PascalCase for types/traits', 'SCREAMING_SNAKE_CASE for constants'],
        customConventions: 'All handlers must return Result<HttpResponse, AppError>. Use SQLx compile-time checked queries. No unwrap() in production code — use proper error handling with ? operator. All public functions must have doc comments. Use derive macros for Serialize/Deserialize. Database migrations with sqlx-cli. Integration tests use a dedicated test database with transaction rollback.',
      },
      mcpConfig: {
        ticketSystem: 'GitHub Issues',
        documentation: ['Markdown in repo'],
        communication: ['Slack'],
        sourceControl: 'GitHub',
        cloudConsole: 'AWS Console',
        monitoring: ['Prometheus', 'Grafana', 'Jaeger'],
        customServers: [],
      },
      cicd: {
        platform: 'GitHub Actions',
        stages: ['Lint (clippy)', 'Format Check', 'Unit Tests', 'Integration Tests', 'Security Audit (cargo-audit)', 'Build Release Binary', 'Build Docker Image', 'Deploy Staging', 'Deploy Production'],
        autoDeploy: 'staging',
        aiCodeFlag: true,
        codeOwnership: true,
      },
      plugins: ['plannotator'],
    },
  },
];

export default function TemplatesPage() {
  const [templates, setTemplates] = useState<Template[]>(demoTemplates);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sort, setSort] = useState('stars');
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setLoading(true);
        const data = await fetchTemplates({
          category: selectedCategory !== 'All' ? selectedCategory : undefined,
          search: search || undefined,
          sort,
        });
        if (data.templates && data.templates.length > 0) {
          setTemplates(data.templates);
        }
      } catch {
        // Use demo data on error
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, [selectedCategory, sort, search]);

  // Client-side filtering for demo
  const filtered = templates.filter((t) => {
    const matchesSearch =
      !search ||
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.description.toLowerCase().includes(search.toLowerCase()) ||
      t.tags.some((tag) => tag.toLowerCase().includes(search.toLowerCase()));
    const matchesCategory =
      selectedCategory === 'All' || t.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sort === 'stars') return b.stars - a.stars;
    if (sort === 'forks') return b.forks - a.forks;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });

  return (
    <div className="min-h-screen pt-28 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-white mb-2">
            Template Gallery
          </h1>
          <p className="text-gray-400">
            Battle-tested configurations from the community. Fork, customize,
            and ship.
          </p>
        </div>

        {/* Search & Controls */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search templates..."
              className="input-field pl-11"
            />
          </div>
          <div className="flex gap-3">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="select-field w-44"
            >
              {sortOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={clsx(
                'btn-secondary px-4 lg:hidden',
                showFilters && 'bg-accent/10 border-accent/30 text-accent'
              )}
            >
              <SlidersHorizontal className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="flex gap-8">
          {/* Filter Sidebar */}
          <aside
            className={clsx(
              'w-52 shrink-0 space-y-6',
              showFilters ? 'block' : 'hidden lg:block'
            )}
          >
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Category
              </h3>
              <div className="space-y-1">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={clsx(
                      'w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-150',
                      selectedCategory === cat
                        ? 'bg-accent/10 text-accent font-medium'
                        : 'text-gray-400 hover:text-gray-300 hover:bg-surface-50'
                    )}
                  >
                    {cat}
                    <span className="ml-2 text-xs text-gray-600">
                      (
                      {cat === 'All'
                        ? templates.length
                        : templates.filter((t) => t.category === cat).length}
                      )
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* Template Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div
                    key={i}
                    className="card animate-pulse h-64"
                  >
                    <div className="h-5 bg-surface-200 rounded w-20 mb-4" />
                    <div className="h-5 bg-surface-200 rounded w-3/4 mb-3" />
                    <div className="space-y-2">
                      <div className="h-3 bg-surface-200 rounded w-full" />
                      <div className="h-3 bg-surface-200 rounded w-2/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : sorted.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-gray-500 text-lg mb-2">
                  No templates found
                </p>
                <p className="text-gray-600 text-sm">
                  Try adjusting your search or filters.
                </p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {sorted.map((template) => (
                  <TemplateCard key={template.id} template={template} />
                ))}
              </div>
            )}

            {/* Pagination placeholder */}
            {sorted.length > 0 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <button className="btn-ghost px-4 py-2 text-sm" disabled>
                  Previous
                </button>
                <span className="px-4 py-2 text-sm text-gray-500">
                  Page 1 of 1
                </span>
                <button className="btn-ghost px-4 py-2 text-sm" disabled>
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
